import { useEffect, useState } from 'react'

// The filter row above a Membership grid: free text (debounced), one select per field,
// the "include inactive" tick (deceased / gone away / archived are hidden by default —
// Chris, 2026-09-22), Save view and Reset.

export interface FilterOption { value: string; label: string }
export interface FilterDef { key: string; label: string; options?: FilterOption[]; kind?: 'select' | 'text' | 'date'; placeholder?: string; width?: number }

export interface FilterBarProps {
  q: string
  onQ: (q: string) => void
  defs: FilterDef[]
  values: Record<string, string>
  onChange: (key: string, value: string) => void
  includeInactive: boolean
  onIncludeInactive: (v: boolean) => void
  inactiveLabel?: string
  onSaveView: () => Promise<void> | void
  onReset: () => void
  savedAt: string | null
  searchPlaceholder?: string
  showInactive?: boolean
}

export function FilterBar({ q, onQ, defs, values, onChange, includeInactive, onIncludeInactive, inactiveLabel = 'Include deceased, gone away and archived', onSaveView, onReset, savedAt, searchPlaceholder = 'Search…', showInactive = true }: FilterBarProps) {
  const [draft, setDraft] = useState(q)
  useEffect(() => { setDraft(q) }, [q])
  useEffect(() => {
    if (draft === q) return
    const id = setTimeout(() => onQ(draft), 350)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState<string | null>(null)
  const active = Object.values(values).filter((v) => v !== '').length + (q ? 1 : 0)

  return (
    <div className="mbr-filters">
      <label className="mbr-field mbr-field--search">Search
        <input type="search" value={draft} placeholder={searchPlaceholder} onChange={(e) => setDraft(e.target.value)} />
      </label>
      {defs.map((d) => (
        <label key={d.key} className="mbr-field" style={d.width ? { minWidth: d.width } : undefined}>{d.label}
          {d.kind === 'text' || d.kind === 'date' ? (
            <input type={d.kind === 'date' ? 'date' : 'text'} value={values[d.key] ?? ''} placeholder={d.placeholder} onChange={(e) => onChange(d.key, e.target.value)} />
          ) : (
            <select value={values[d.key] ?? ''} onChange={(e) => onChange(d.key, e.target.value)}>
              <option value="">Any</option>
              {(d.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
        </label>
      ))}
      {showInactive && (
        <label className="mbr-check" title="Deceased, gone-away and archived members (and lapsed, cancelled and test memberships) are hidden unless ticked.">
          <input type="checkbox" checked={includeInactive} onChange={(e) => onIncludeInactive(e.target.checked)} /> {inactiveLabel}
        </label>
      )}
      <span className="mbr-spacer" />
      <span className="mbr-filter-actions">
        {active > 0 && <button type="button" className="btn-link" onClick={onReset}>Clear {active} filter{active === 1 ? '' : 's'}</button>}
        <button type="button" className="btn-secondary" disabled={saving} title="Remember this sort order, page size and these filters for you, on this grid."
                onClick={async () => { setSaving(true); setSaveMsg(null); try { await onSaveView(); setSaveMsg('Saved') } catch (e) { setSaveMsg(String((e as Error)?.message ?? e)) } finally { setSaving(false); setTimeout(() => setSaveMsg(null), 3000) } }}>
          {saving ? 'Saving…' : 'Save view'}
        </button>
        {saveMsg && <span className="mbr-note">{saveMsg}</span>}
        {!saveMsg && savedAt && <span className="mbr-note" title={savedAt}>Your saved view is on</span>}
      </span>
    </div>
  )
}
